import { useState, type ReactElement } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMore from "@mui/icons-material/ExpandMore";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { SUPPORTED_UI_LANGUAGES, type UiLanguage } from "../../i18n";
import { useUiLanguage } from "../../i18n/useUiLanguage";

function GbFlag() {
  return (
    <svg viewBox="0 0 60 30" width="22" height="15" aria-hidden>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 60,30 M60,0 0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 60,30 M60,0 0,30" stroke="#C8102E" strokeWidth="2" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

function TrFlag() {
  return (
    <svg viewBox="0 0 1200 800" width="22" height="15" aria-hidden>
      <rect width="1200" height="800" fill="#E30A17" />
      <circle cx="425" cy="400" r="200" fill="#fff" />
      <circle cx="475" cy="400" r="160" fill="#E30A17" />
      <polygon
        fill="#fff"
        points="583.334,400 764.035,458.779 652.434,304.894 652.434,495.106 764.035,341.221"
      />
    </svg>
  );
}

type LanguageOption = {
  value: UiLanguage;
  label: string;
  Flag: () => ReactElement;
};

const FLAG_BY_LANGUAGE: Record<UiLanguage, () => ReactElement> = {
  en: GbFlag,
  tr: TrFlag,
};

const LABEL_KEY_BY_LANGUAGE: Record<UiLanguage, string> = {
  en: "configurator.english",
  tr: "configurator.turkish",
};

const SHORT_LABEL_BY_LANGUAGE: Record<UiLanguage, string> = {
  en: "EN",
  tr: "TR",
};

type LanguageSwitcherProps = {
  compact?: boolean;
  /** Flag and short code that open a menu. Used on narrow screens. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  label?: string;
  sx?: SxProps<Theme>;
};

export default function LanguageSwitcher({
  compact = false,
  iconOnly = false,
  fullWidth = false,
  label,
  sx,
}: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { language, setLanguage } = useUiLanguage();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const options: LanguageOption[] = SUPPORTED_UI_LANGUAGES.map((code) => ({
    value: code,
    label: t(LABEL_KEY_BY_LANGUAGE[code]),
    Flag: FLAG_BY_LANGUAGE[code],
  }));

  const selected = options.find((option) => option.value === language) ?? options[0];
  const SelectedFlag = selected.Flag;

  if (iconOnly) {
    return (
      <>
        <Button
          aria-label={t("configurator.language")}
          aria-haspopup="listbox"
          aria-expanded={Boolean(menuAnchor)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          startIcon={
            <Box sx={{ display: "flex", "& svg": { display: "block", borderRadius: "2px" } }}>
              <SelectedFlag />
            </Box>
          }
          endIcon={<ExpandMore sx={{ fontSize: 20 }} />}
          sx={{
            minWidth: 0,
            height: 44,
            px: 1.25,
            flexShrink: 0,
            borderRadius: 1.5,
            border: 1,
            borderColor: "divider",
            color: "text.primary",
            textTransform: "none",
            fontWeight: 700,
            letterSpacing: 0,
            "& .MuiButton-startIcon": { mr: 0.75, ml: 0 },
            "& .MuiButton-endIcon": { ml: 0.25, mr: -0.25 },
            ...sx,
          }}
        >
          {SHORT_LABEL_BY_LANGUAGE[selected.value]}
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {options.map((option) => {
            const Flag = option.Flag;
            return (
              <MenuItem
                key={option.value}
                selected={option.value === language}
                onClick={() => {
                  void setLanguage(option.value);
                  setMenuAnchor(null);
                }}
                sx={{ minHeight: 48, gap: 1.5, px: 2 }}
              >
                <Box sx={{ display: "flex", "& svg": { display: "block", borderRadius: "2px" } }}>
                  <Flag />
                </Box>
                <Typography variant="body2">{option.label}</Typography>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  return (
    <Autocomplete
      options={options}
      value={selected}
      disableClearable
      size="small"
      fullWidth={fullWidth}
      sx={{ minWidth: compact ? 148 : 180, ...sx }}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, next) => option.value === next.value}
      onChange={(_event, next) => {
        if (next) void setLanguage(next.value);
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        const Flag = option.Flag;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box sx={{ display: "flex", "& svg": { display: "block", borderRadius: "2px" } }}>
              <Flag />
            </Box>
            <Typography variant="body2">{option.label}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={compact ? undefined : label}
          aria-label={label || t("configurator.language")}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <Box sx={{ display: "flex", "& svg": { display: "block", borderRadius: "2px" } }}>
                    <SelectedFlag />
                  </Box>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        />
      )}
    />
  );
}
